import React, {useState, useCallback, MouseEvent, Dispatch} from 'react';
import wrap from 'wraptile';
import {curry} from 'ramda';
import {ApTimelineGroup} from '@accurate-player/accurate-player-components-external';
import {FrameRate} from '@accurate-player/accurate-player-core';

import {ButtonClickEvent} from 'VideoAudioQC/Timeline/TimelineTypes';
import AudioTrack from './AudioTrack';
import {
    AudioChannelClick,
    AudioTrackClick,
    AudioTrackData,
} from '../AudioTracksTypes';

export type SetCollapsedAction = Dispatch<React.SetStateAction<boolean>>;

export interface AudioTrackContainerProps {
    trackData: AudioTrackData;
    onlyMonoAudioAvailable: boolean;
    frameRate: FrameRate;
    onAudioTrackClick: AudioTrackClick;
    onAudioChannelClick: AudioChannelClick;
}

export const toggle = (value: boolean) => !value;

export const preventEvent = (event: MouseEvent) => {
    if (!event)
        return;
    
    event.preventDefault();
    event.stopPropagation();
};

export const toggleCollapse = wrap((setCollapsed: SetCollapsedAction) => {
    setCollapsed(toggle);
});

export const toggleMuteChannel = curry((props: AudioTrackContainerProps, channel: number, event: ButtonClickEvent) => {
    const {trackData, onAudioChannelClick} = props;
    
    onAudioChannelClick(trackData, channel);
    
    event.stopPropagation();
    event.preventDefault();
});

export const toggleMute = curry((props: AudioTrackContainerProps, event: ButtonClickEvent) => {
    const {trackData, onAudioTrackClick} = props;
    
    onAudioTrackClick(trackData);
    
    event.stopPropagation();
    event.preventDefault();
});

export const handleAudioTrackChanged = curry((collapsed: boolean, node: ApTimelineGroup) => {
    if (!node)
        return;
    
    node.collapsed = collapsed;
});

const useAudioTrackRef = (collapsed: boolean) => {
    const onTrackChange = handleAudioTrackChanged(collapsed);
    const setRef = useCallback(onTrackChange, [collapsed]);
    
    return [setRef];
};

export default function AudioTrackContainer(props: AudioTrackContainerProps) {
    const {
        trackData,
        onlyMonoAudioAvailable,
        frameRate,
        onAudioTrackClick,
    } = props;
    const [collapsed, setCollapsed] = useState(false);
    const handleCollapsed = toggleCollapse(setCollapsed);
    const handleMute = toggleMute(props);
    const handleMuteChannel = toggleMuteChannel(props);
    const [setAudioTrackRef] = useAudioTrackRef(collapsed);
    
    return (
        <AudioTrack
            onlyMonoAudioAvailable={onlyMonoAudioAvailable}
            trackData={trackData}
            collapsed={collapsed}
            frameRate={frameRate}
            setAudioTrackRef={setAudioTrackRef}
            onToggleCollapse={handleCollapsed}
            onToggleMute={handleMute}
            onToggleMuteChannel={handleMuteChannel}
            onChannelRowClick={preventEvent}
            onAudioTrackClick={onAudioTrackClick}
        />
    );
}
